import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import classNames from "classnames";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { Checkbox } from "primereact/checkbox";
import { useDispatch } from "react-redux";
import { handlePostRequest } from "../../../services/PostTemplate";
import { handleGetRequest } from "../../../services/GetTemplate";
import { toast } from "react-toastify";
import styles from "./MakeDeliveryDialog.module.scss";
import { setDeliveryId } from "../../../redux/routeFLowSlice";

function MakeDeliveryDialog({ onHideDialog, onSuccess, editData = null }) {
    const dispatch = useDispatch();
    const [handlers, setHandlers] = useState([]);
    const [drivers, setDrivers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [nextDeliveryId, setNextDeliveryId] = useState("");
    const [currentUserId, setCurrentUserId] = useState(null);
    const [currentUserName, setCurrentUserName] = useState("");

    // Checkbox states
    const [addMeAsHandler, setAddMeAsHandler] = useState(false);
    const [addMeAsDriver, setAddMeAsDriver] = useState(false);

    // Delivery type options
    const deliveryTypeOptions = [
        { label: "Local Courier", value: "local courier" },
        { label: "Driver", value: "driver" },
        { label: "Lockbox", value: "lockbox" },
    ];

    const isDriver = localStorage.getItem("role") === "driver";

    // Get current user info from localStorage
    useEffect(() => {
        const userId = localStorage.getItem("userId");
        const firstName = localStorage.getItem("firstName") || "";
        const lastName = localStorage.getItem("lastName") || "";
        const userName = `${firstName} ${lastName}`.trim() || "Current User";

        console.log("Current user:", { userId, userName });

        setCurrentUserId(userId);
        setCurrentUserName(userName);
    }, []);

    // Fetch handlers, drivers, and generate next delivery ID
    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);

            // Fetch handlers
            const handlersRes = await handleGetRequest("/userRole?role=handler");
            const handlersList = handlersRes?.data || [];
            const formattedHandlers = handlersList.map((handler) => ({
                label: `${handler.firstName} ${handler.lastName}`,
                value: handler._id,
                name: `${handler.firstName} ${handler.lastName}`,
            }));
            setHandlers(formattedHandlers);

            // Fetch drivers
            const driversRes = await handleGetRequest("/userRole?role=driver");
            const driversList = driversRes?.data || [];
            const formattedDrivers = driversList.map((driver) => ({
                label: `${driver.firstName} ${driver.lastName}`,
                value: driver._id,
                name: `${driver.firstName} ${driver.lastName}`,
            }));
            setDrivers(formattedDrivers);

            // Generate next delivery ID if not editing
            if (!editData) {
                const lastDeliveryRes = await handleGetRequest("/getLastDelivery");
                const lastDelivery = lastDeliveryRes?.data;

                if (lastDelivery && lastDelivery.deliveryId) {
                    const lastNumber = parseInt(lastDelivery.deliveryId.replace("DLV", ""));
                    const nextNumber = lastNumber + 1;
                    setNextDeliveryId(`DLV${String(nextNumber).padStart(3, "0")}`);
                } else {
                    setNextDeliveryId("DLV001");
                }
            }
        } catch (error) {
            console.error("Error fetching data:", error);
            toast.error("Failed to load form data");
        } finally {
            setLoading(false);
        }
    };

    // Validation schema
    const validationSchema = Yup.object().shape({
        handlerId: Yup.string().required("Handler is required"),
        driverId: Yup.string().required("Driver is required"),
        deliveryType: Yup.string().required("Delivery type is required"),
    });

    // Form initialization
    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            deliveryId: editData?.deliveryId || nextDeliveryId,
            handlerId: editData?.handlerId || (isDriver ? currentUserId : ""),
            handlerName: editData?.handlerName || (isDriver ? currentUserName : ""),
            driverId: editData?.driverId || (isDriver ? currentUserId : ""),
            driverName: editData?.driverName || (isDriver ? currentUserName : ""),
            deliveryType: editData?.deliveryType || "",
        },
        validationSchema: validationSchema,
        onSubmit: async (data) => {
            try {
                setLoading(true);

                // Find selected handler and driver names
                const selectedHandler = handlers.find((h) => h.value === data.handlerId);
                const selectedDriver = drivers.find((d) => d.value === data.driverId);

                const payload = {
                    deliveryId: data.deliveryId,
                    handlerId: data.handlerId,
                    handlerName: selectedHandler?.name || data.handlerName,
                    driverId: data.driverId,
                    driverName: selectedDriver?.name || data.driverName,
                    deliveryType: data.deliveryType,
                };

                console.log("Submitting delivery:", payload);

                let result;
                if (editData) {
                    result = await dispatch(handlePostRequest({ ...payload, _id: editData._id }, "/updateDelivery", true, true));
                } else {
                    result = await dispatch(handlePostRequest(payload, "/createDelivery", true, true));
                }

                if (result !== "error") {
                    toast.success(editData ? "Delivery updated successfully" : "Delivery created successfully");
                    console.log("Delivery saved:", result?.data);
                    dispatch(setDeliveryId(result?.data?._id));
                    onSuccess(result?.data?._id, result?.data?.status);
                } else {
                    toast.error(editData ? "Failed to update delivery" : "Failed to create delivery");
                }
            } catch (error) {
                console.error("Error submitting form:", error);
                toast.error("An error occurred while saving delivery");
            } finally {
                setLoading(false);
            }
        },
    });

    const isFormFieldValid = (name) => !!(formik.touched[name] && formik.errors[name]);
    const getFormErrorMessage = (name) => {
        return isFormFieldValid(name) && <small className="p-error">{formik.errors[name]}</small>;
    };

    // Handle "Add me as Handler" checkbox
    const handleAddMeAsHandler = (checked) => {
        setAddMeAsHandler(checked);
        if (checked && currentUserId && currentUserName) {
            // Set current user as handler
            formik.setFieldValue("handlerId", currentUserId);
            formik.setFieldValue("handlerName", currentUserName);
            console.log("Added current user as handler:", { currentUserId, currentUserName });
        } else if (!checked) {
            // Clear handler fields when unchecked
            formik.setFieldValue("handlerId", "");
            formik.setFieldValue("handlerName", "");
        }
    };

    // Handle "Add me as Driver" checkbox
    const handleAddMeAsDriver = (checked) => {
        setAddMeAsDriver(checked);
        if (checked && currentUserId && currentUserName) {
            // Set current user as driver
            formik.setFieldValue("driverId", currentUserId);
            formik.setFieldValue("driverName", currentUserName);
            console.log("Added current user as driver:", { currentUserId, currentUserName });
        } else if (!checked) {
            // Clear driver fields when unchecked
            formik.setFieldValue("driverId", "");
            formik.setFieldValue("driverName", "");
        }
    };

    // Update checkbox state when dropdown changes manually (only when NOT checked)
    useEffect(() => {
        if (!addMeAsHandler) {
            // Only update checkbox if it's not already checked
            if (formik.values.handlerId === currentUserId) {
                setAddMeAsHandler(true);
            }
        }
    }, [formik.values.handlerId, currentUserId, addMeAsHandler]);

    useEffect(() => {
        if (!addMeAsDriver) {
            // Only update checkbox if it's not already checked
            if (formik.values.driverId === currentUserId) {
                setAddMeAsDriver(true);
            }
        }
    }, [formik.values.driverId, currentUserId, addMeAsDriver]);

    return (
        <div className={styles.dialogContainer}>
            <div className={styles.dialogHeader}>
                <h3>{editData ? "Edit Delivery" : "Create New Delivery"}</h3>
                <p className={styles.subtitle}>{editData ? "Update delivery information" : "Fill in the details to create a new delivery"}</p>
            </div>

            <form onSubmit={formik.handleSubmit} className={styles.form}>
                {/* Delivery ID (Read-only) */}
                <div className={styles.formField}>
                    <label htmlFor="deliveryId" className={styles.label}>
                        Delivery ID
                    </label>
                    <InputText id="deliveryId" name="deliveryId" value={formik.values.deliveryId} disabled className={styles.input} placeholder="Auto-generated" />
                    <small className={styles.helpText}>{editData ? "Delivery ID cannot be changed" : "Automatically generated"}</small>
                </div>

                {/* Drivers are always their own handler and driver */}
                {!isDriver && (
                    <>
                {/* Add Me as Handler Checkbox - ABOVE Handler Dropdown */}
                {currentUserId && (
                    <div className={classNames(styles.topCheckboxWrapper, { [styles.topCheckboxActive]: addMeAsHandler })}>
                        <Checkbox inputId="addMeAsHandler" checked={addMeAsHandler} onChange={(e) => handleAddMeAsHandler(e.checked)} disabled={loading} className={styles.topCheckbox} />
                        <label htmlFor="addMeAsHandler" className={styles.topCheckboxLabel}>
                            <i className="pi pi-user-plus" />
                            Add me as a handler
                        </label>
                    </div>
                )}

                {/* Handler Dropdown */}
                <div className={styles.formField}>
                    <label htmlFor="handlerId" className={classNames(styles.label, { [styles.errorLabel]: isFormFieldValid("handlerId") })}>
                        Handler <span className={styles.required}>*</span>
                        {addMeAsHandler && (
                            <span className={styles.lockedBadge}>
                                <i className="pi pi-lock" />
                                Locked
                            </span>
                        )}
                    </label>
                    <Dropdown
                        id="handlerId"
                        name="handlerId"
                        value={formik.values.handlerId}
                        options={handlers}
                        onChange={(e) => {
                            formik.setFieldValue("handlerId", e.value);
                            const selected = handlers.find((h) => h.value === e.value);
                            if (selected) {
                                formik.setFieldValue("handlerName", selected.name);
                            }
                        }}
                        onBlur={formik.handleBlur}
                        placeholder={addMeAsHandler ? currentUserName : "Select a handler"}
                        className={classNames(styles.dropdown, {
                            [styles.invalidField]: isFormFieldValid("handlerId"),
                            [styles.disabledDropdown]: addMeAsHandler,
                        })}
                        filter
                        filterPlaceholder="Search handlers..."
                        emptyMessage="No handlers found"
                        disabled={loading || addMeAsHandler} // DISABLED when checkbox is checked
                        itemTemplate={(option) => (
                            <div className={styles.dropdownItem}>
                                <span>{option.label}</span>
                                {option.value === currentUserId && <span className={styles.currentUserTag}>You</span>}
                            </div>
                        )}
                    />
                    {getFormErrorMessage("handlerId")}
                    {addMeAsHandler && formik.values.handlerId === currentUserId && (
                        <small className={styles.selectedAsCurrentUser}>
                            <i className="pi pi-check-circle" />
                            You are set as the handler (uncheck to change)
                        </small>
                    )}
                </div>

                {/* Add Me as Driver Checkbox - ABOVE Driver Dropdown */}
                {currentUserId && (
                    <div className={classNames(styles.topCheckboxWrapper, { [styles.topCheckboxActive]: addMeAsDriver })}>
                        <Checkbox inputId="addMeAsDriver" checked={addMeAsDriver} onChange={(e) => handleAddMeAsDriver(e.checked)} disabled={loading} className={styles.topCheckbox} />
                        <label htmlFor="addMeAsDriver" className={styles.topCheckboxLabel}>
                            <i className="pi pi-user-plus" />
                            Add me as a driver
                        </label>
                    </div>
                )}

                {/* Driver Dropdown */}
                <div className={styles.formField}>
                    <label htmlFor="driverId" className={classNames(styles.label, { [styles.errorLabel]: isFormFieldValid("driverId") })}>
                        Driver <span className={styles.required}>*</span>
                        {addMeAsDriver && (
                            <span className={styles.lockedBadge}>
                                <i className="pi pi-lock" />
                                Locked
                            </span>
                        )}
                    </label>
                    <Dropdown
                        id="driverId"
                        name="driverId"
                        value={formik.values.driverId}
                        options={drivers}
                        onChange={(e) => {
                            formik.setFieldValue("driverId", e.value);
                            const selected = drivers.find((d) => d.value === e.value);
                            if (selected) {
                                formik.setFieldValue("driverName", selected.name);
                            }
                        }}
                        onBlur={formik.handleBlur}
                        placeholder={addMeAsDriver ? currentUserName : "Select a driver"}
                        className={classNames(styles.dropdown, {
                            [styles.invalidField]: isFormFieldValid("driverId"),
                            [styles.disabledDropdown]: addMeAsDriver,
                        })}
                        filter
                        filterPlaceholder="Search drivers..."
                        emptyMessage="No drivers found"
                        disabled={loading || addMeAsDriver} // DISABLED when checkbox is checked
                        itemTemplate={(option) => (
                            <div className={styles.dropdownItem}>
                                <span>{option.label}</span>
                                {option.value === currentUserId && <span className={styles.currentUserTag}>You</span>}
                            </div>
                        )}
                    />
                    {getFormErrorMessage("driverId")}
                    {addMeAsDriver && formik.values.driverId === currentUserId && (
                        <small className={styles.selectedAsCurrentUser}>
                            <i className="pi pi-check-circle" />
                            You are set as the driver (uncheck to change)
                        </small>
                    )}
                </div>

                    </>
                )}
                {/* Delivery Type Dropdown */}
                <div className={styles.formField}>
                    <label
                        htmlFor="deliveryType"
                        className={classNames(styles.label, {
                            [styles.errorLabel]: isFormFieldValid("deliveryType"),
                        })}
                    >
                        Delivery Type <span className={styles.required}>*</span>
                    </label>
                    <Dropdown
                        id="deliveryType"
                        name="deliveryType"
                        value={formik.values.deliveryType}
                        options={deliveryTypeOptions}
                        onChange={(e) => formik.setFieldValue("deliveryType", e.value)}
                        onBlur={formik.handleBlur}
                        placeholder="Select delivery type"
                        className={classNames(styles.dropdown, {
                            [styles.invalidField]: isFormFieldValid("deliveryType"),
                        })}
                        disabled={loading}
                    />
                    {getFormErrorMessage("deliveryType")}
                </div>

                {/* Form Actions */}
                <div className={styles.formActions}>
                    <Button label="Cancel" type="button" className={styles.cancelButton} onClick={onHideDialog} disabled={loading} />
                    <Button label={loading ? "Saving..." : editData ? "Update Delivery" : "Create Delivery"} type="submit" className={styles.submitButton} disabled={loading} icon={loading ? "pi pi-spin pi-spinner" : "pi pi-check"} />
                </div>
            </form>
        </div>
    );
}

export default MakeDeliveryDialog;
